import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col, Button, Card, Table, Modal } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { GiMoneyStack, GiReceiveMoney } from "react-icons/gi";
import { MdCurrencyExchange, MdExpandMore } from "react-icons/md";
import CountUp from "react-countup";
import "./RevenueRoiReport.css";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const RevenueRoiReport = () => {
    const { adminUser } = useAdminAuth();
    const [reportData, setReportData] = useState([]);
    const [totals, setTotals] = useState({});
    const [fundingAgencyTotals, setFundingAgencyTotals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fundingAgencies, setFundingAgencies] = useState([]);
    const [branches, setBranches] = useState([]);
    const [showMonthlyModal, setShowMonthlyModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
    const [currentPage, setCurrentPage] = useState(1);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Filters
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedFundingAgency, setSelectedFundingAgency] = useState(null);
    const [selectedLoanType, setSelectedLoanType] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const itemsPerPage = 20;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const filterData = (data) => {
        let filtered = data;
        if (searchTerm && searchTerm.trim() !== "") {
            const term = searchTerm.trim().toLowerCase();
            filtered = filtered.filter((item) => {
                const memberName = String(item?.memberName || "").toLowerCase();
                const customerId = String(item?.customerId || "").toLowerCase();
                const loanId = String(item?.loanId || "").toLowerCase();
                const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
                const aadharNo = String(item?.aadharNo || "").toLowerCase();
                const applicationId = String(item?.ApplicationId || "").toLowerCase();

                return (
                    memberName.includes(term) ||
                    customerId.includes(term) ||
                    loanId.includes(term) ||
                    phoneNumber.includes(term) ||
                    aadharNo.includes(term) ||
                    applicationId.includes(term)
                );
            });
        }
        return filtered;
    };

    const filteredData = filterData(reportData);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const OpenSidebar = () => {
        setOpenSidebarToggle(!openSidebarToggle);
    };

    // Loan Type Options
    const loanTypeOptions = [
        { value: "All", label: "All Loan Types" },
        { value: "Business Loan", label: "Business Loan" },
        { value: "JLG Loan", label: "JLG Loan" },
        { value: "GP Loan", label: "GP Loan" },
    ];

    // Fetch funding agencies
    useEffect(() => {
        const fetchFundingAgencies = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/getModelData/funding_agencies`);
                if (response.data && response.data.data) {
                    const agencies = response.data.data.map((agency) => ({
                        value: agency.id,
                        label: agency.agencyName,
                    }));
                    setFundingAgencies([{ value: "All", label: "All Funding Agencies" }, ...agencies]);
                }
            } catch (error) {
                console.log("Error fetching funding agencies:", error);
            }
        };
        fetchFundingAgencies();
    }, []);

    // Fetch branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/getall/branches`);
                if (response.data) {
                    const branchList = response.data.map((branch) => ({
                        value: branch.id,
                        label: branch.branchName,
                    }));
                    setBranches([{ value: "All", label: "All Branches" }, ...branchList]);
                }
            } catch (error) {
                console.log("Error fetching branches:", error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch report data on filter change
    useEffect(() => {
        const fetchReportDataAuto = async () => {
            setLoading(true);
            try {
                const params = {
                    id: adminUser.user.id,
                    loanType: selectedLoanType?.value || "All",
                    fundingAgencyId: selectedFundingAgency?.value || "All",
                    branchId: selectedBranch?.value || "All",
                    includeMonthlyBreakdown: "true",
                };

                if (fromDate && toDate) {
                    params.startDate = fromDate;
                    params.endDate = toDate;
                }

                const response = await axios.get(
                    `${BASE_URL}/api/getRevenueRoiReportData`,
                    { params }
                );

                if (response.data) {
                    // Backend now sends accurate calculations
                    setReportData(response.data.data || []);
                    setTotals(response.data.totals || {});
                    setFundingAgencyTotals(response.data.fundingAgencyTotals || []);
                } else {
                    console.log("Error in fetching Data");
                }
            } catch (error) {
                console.log("Error fetching Data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchReportDataAuto();
    }, [fromDate, toDate, selectedLoanType, selectedFundingAgency, selectedBranch, adminUser.user.id]);

    const handleViewMonthlyBreakdown = (customer) => {
        setSelectedCustomer(customer);
        setShowMonthlyModal(true);
    };

    // Define columns - Backend now sends accurate calculations
    const customerColumns = [
        { label: "S No", render: (data, index) => indexOfFirstItem + index + 1 },
        { label: "Application ID", render: (data) => data.ApplicationId || "N/A" },
        { label: "Member Name", render: (data) => data.memberName || "N/A" },
        { label: "Loan Type", render: (data) => data.loanType || "N/A" },
        { label: "Funding Agency", render: (data) => data.fundingAgencyName || "N/A" },
        { label: "Branch", render: (data) => data.branchName || "N/A" },
        {
            label: "Loan Amount",
            render: (data) =>
                "₹" + Number(data.loanAmount || 0).toLocaleString("en-IN"),
        },
        {
            label: "ROI (%)",
            render: (data) => Number(data.rateOfInterest || 0).toFixed(2),
        },
        {
            label: "Revenue %",
            render: (data) => Number(data.revenueInterestPercentage || 0).toFixed(2),
        },
        {
            label: "Tenure",
            render: (data) => data.tenureInMonths || "N/A",
        },
        {
            label: "Monthly EMI",
            render: (data) =>
                "₹" + Number(data.monthlyEmi || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Monthly Interest (1st Month)",
            render: (data) =>
                "₹" + Number(data.monthlyInterest || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Monthly Revenue (1st Month)",
            render: (data) =>
                "₹" + Number(data.monthlyRevenueInterest || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Total Revenue Interest",
            render: (data) =>
                "₹" + Number(data.totalInterestRevenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "GST (18%)",
            render: (data) =>
                "₹" + Number(data.gstOnInterest || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Net Revenue (with GST)",
            render: (data) =>
                "₹" + Number(data.netInterestRevenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        },
        {
            label: "Disbursement Date",
            render: (data) =>
                data.disbursementDate
                    ? new Date(data.disbursementDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                    })
                    : "N/A",
        },
        {
            label: "Monthly Details",
            render: (data) => (
                <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => handleViewMonthlyBreakdown(data)}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <>
            <section className="master-layout">
                <div className="master-sidebar-wrapper">
                    <Sidebar
                        openSidebarToggle={openSidebarToggle}
                        OpenSidebar={OpenSidebar}
                    />
                </div>

                <div className="master-content-wrapper">
                    <div className="master-header-wrapper">
                        <DashboardHeader OpenSidebar={OpenSidebar} />
                    </div>
                    <div className="master-card-container">
                        <div className="master-search-section">
                            <h1 className="TableHeaderText w-100 text-start">Revenue - Rate of Interest Report</h1>

                            <button
                                className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
                                onClick={() => setShowMobileFilters(!showMobileFilters)}
                            >
                                <span style={{ fontWeight: 600, fontSize: "14px" }}>
                                    {showMobileFilters ? "Hide Filters" : "Show Filters"}
                                </span>
                                <MdExpandMore size={22} />
                            </button>

                            <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
                                <Row>
                                    <Col xs={12} lg={4} md={6} className="mb-2">
                                        <Form.Group>
                                            <Form.Label>Search Customer</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} lg={2} md={6}>
                                        <Form.Group>
                                            <Form.Label>From Date</Form.Label>
                                            <StandardizedDateInput
                                                value={fromDate}
                                                onChange={(event) => {
                                                    const val = event.target.value;
                                                    setFromDate(val);
                                                    if (val && toDate && selectedFundingAgency && selectedLoanType && selectedBranch) {
                                                        setTimeout(() => setShowMobileFilters(false), 300);
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} lg={2} md={6}>
                                        <Form.Group>
                                            <Form.Label>To Date</Form.Label>
                                            <StandardizedDateInput
                                                value={toDate}
                                                onChange={(event) => {
                                                    const val = event.target.value;
                                                    setToDate(val);
                                                    if (fromDate && val && selectedFundingAgency && selectedLoanType && selectedBranch) {
                                                        setTimeout(() => setShowMobileFilters(false), 300);
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} lg={2} md={6}>
                                        <Form.Label>Funding Agency</Form.Label>
                                        <Select
                                            value={selectedFundingAgency}
                                            onChange={(val) => {
                                                setSelectedFundingAgency(val);
                                                if (fromDate && toDate && val && selectedLoanType && selectedBranch) {
                                                    setTimeout(() => setShowMobileFilters(false), 300);
                                                }
                                            }}
                                            options={fundingAgencies}
                                            isClearable
                                            placeholder="Select Agency"
                                            className="filter-dropdown"
                                        />
                                    </Col>
                                    <Col xs={12} lg={2} md={6}>
                                        <Form.Label>Loan Type</Form.Label>
                                        <Select
                                            value={selectedLoanType}
                                            onChange={(val) => {
                                                setSelectedLoanType(val);
                                                if (fromDate && toDate && selectedFundingAgency && val && selectedBranch) {
                                                    setTimeout(() => setShowMobileFilters(false), 300);
                                                }
                                            }}
                                            options={loanTypeOptions}
                                            isClearable
                                            placeholder="Select Type"
                                            className="filter-dropdown"
                                        />
                                    </Col>
                                    <Col xs={12} lg={2} md={6}>
                                        <Form.Label>Branch</Form.Label>
                                        <Select
                                            value={selectedBranch}
                                            onChange={(val) => {
                                                setSelectedBranch(val);
                                                if (fromDate && toDate && selectedFundingAgency && selectedLoanType && val) {
                                                    setTimeout(() => setShowMobileFilters(false), 300);
                                                }
                                            }}
                                            options={branches}
                                            isClearable
                                            placeholder="Select Branch"
                                            className="filter-dropdown"
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>

                        <div className="master-list-section">
                            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={customerColumns}
                                        data={paginationData}
                                        reportName="RevenueRoiReport"
                                        excelData={filteredData}
                                    />
                                )}
                            </div>

                            <div className="master-pagination-wrapper">
                                <CustomPagination
                                    totalPages={totalPages}
                                    currentPage={currentPage}
                                    paginate={paginate}
                                    totalItems={reportData.length}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Monthly Breakdown Modal */}
            <Modal
                show={showMonthlyModal}
                onHide={() => setShowMonthlyModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton style={{ borderBottom: "1px solid #000" }}>
                    <Modal.Title style={{ color: "#000" }}>
                        Monthly EMI Schedule - {selectedCustomer?.memberName} ({selectedCustomer?.ApplicationId})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {selectedCustomer && (
                        <>
                            <Row className="mb-3">
                                <Col md={3}>
                                    <strong>Loan Amount:</strong> ₹{selectedCustomer.loanAmount?.toLocaleString("en-IN")}
                                </Col>
                                <Col md={3}>
                                    <strong>ROI:</strong> {selectedCustomer.rateOfInterest}%
                                </Col>
                                <Col md={3}>
                                    <strong>Revenue %:</strong> {selectedCustomer.revenueInterestPercentage}%
                                </Col>
                                <Col md={3}>
                                    <strong>Tenure:</strong> {selectedCustomer.tenureInMonths} months
                                </Col>
                            </Row>
                            <div className="alert border-dark text-black" style={{ backgroundColor: "var(--bg-card)" }}>
                                <strong>Calculation Method:</strong> Reducing Balance
                                <br />
                                <strong>Formula:</strong>
                                <ul className="mb-0 mt-2">
                                    <li>Monthly Interest = Outstanding Principal × (ROI / 12 / 100)</li>
                                    <li>Revenue Amount = Monthly Interest × (Revenue % / 100)</li>
                                    <li>GST = Revenue Amount × 18%</li>
                                </ul>
                            </div>
                            <Table bordered hover responsive style={{ color: "#000" }}>
                                <thead style={{ backgroundColor: "#000", color: "var(--white)" }}>
                                    <tr>
                                        <th>Month</th>
                                        <th>Outstanding</th>
                                        <th>Monthly EMI</th>
                                        <th>Interest</th>
                                        <th>Principal</th>
                                        <th>Revenue %</th>
                                        <th>Revenue Amount</th>
                                        <th>GST (18%)</th>
                                        <th>Net Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedCustomer.monthlySchedule && selectedCustomer.monthlySchedule.map((schedule, idx) => (
                                        <tr key={idx}>
                                            <td>{schedule.monthNumber}</td>
                                            <td>₹{schedule.outstandingPrincipalStart?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                                            <td>₹{schedule.monthlyEmi?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                                            <td>₹{schedule.monthlyInterest?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                                            <td>₹{schedule.principalComponent?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                                            <td>{selectedCustomer.revenueInterestPercentage}%</td>
                                            <td className="fw-bold">
                                                ₹{schedule.monthlyRevenueInterest?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td>
                                                ₹{schedule.gstOnMonthlyRevenue?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="fw-bold">
                                                ₹{schedule.netMonthlyRevenue?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-secondary">
                                    <tr>
                                        <td colSpan="2"><strong>Total ({selectedCustomer.tenureInMonths} months)</strong></td>
                                        <td>
                                            <strong>
                                                ₹{selectedCustomer.monthlySchedule?.reduce((sum, s) => sum + (s.monthlyEmi || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </strong>
                                        </td>
                                        <td>
                                            <strong>
                                                ₹{selectedCustomer.totalInterest?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </strong>
                                        </td>
                                        <td>
                                            <strong>
                                                ₹{selectedCustomer.monthlySchedule?.reduce((sum, s) => sum + (s.principalComponent || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </strong>
                                        </td>
                                        <td></td>
                                        <td>
                                            <strong>
                                                ₹{selectedCustomer.totalInterestRevenue?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </strong>
                                        </td>
                                        <td>
                                            <strong>
                                                ₹{selectedCustomer.gstOnInterest?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </strong>
                                        </td>
                                        <td>
                                            <strong>
                                                ₹{selectedCustomer.netInterestRevenue?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </strong>
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMonthlyModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RevenueRoiReport;